import { ChevronLeft, X, ExternalLink, AlertCircle, Zap, Lock, RefreshCw } from "lucide-react";
import {
  UseFormRegister,
  FieldValues,
  UseFormHandleSubmit,
  FieldErrors,
  Control,
} from "react-hook-form";
import { FormInput } from "../components/forms/Input";
import { FormSelect } from "../components/forms/Select";
import { FormNumberInput } from "../components/forms/NumberInput";
import { FormPassword } from "../components/forms/Password";
import { FormTextArea } from "../components/forms/TextArea";
import { ConnectionDefinition } from "../types/integrations";
import { VscQuestion } from "react-icons/vsc";
import { FormSwitch } from "../components/forms/Switch";
import MarkdownRenderer from "../components/MarkdownRenderer";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface IProps {
  definition: ConnectionDefinition;
  onClose: () => void;
  onSubmit: (data: { [K: string]: string }) => void;
  onBack?: () => void;
  register: UseFormRegister<FieldValues>;
  control?: Control<FieldValues, any>
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  errors: FieldErrors<FieldValues>;
  error?: string;
  showNameInput?: boolean;
  isLoading?: boolean;
  isConnectionSelected?: boolean;
  helperLink?: string;
  platformImageUrl?: string;
  showAvatars?: boolean;
  companyName?: string;
  isWhiteLabel?: boolean | null;
}

export const ConnectionFormSection = ({
  definition,
  onClose,
  onBack,
  register,
  control,
  handleSubmit,
  onSubmit,
  errors,
  error,
  showNameInput,
  isLoading,
  isConnectionSelected,
  helperLink,
  platformImageUrl,
  showAvatars,
  companyName,
  isWhiteLabel,
}: IProps) => {

  const formData = showNameInput
    ? [
        {
          name: "NAME",
          type: "input",
          label: "Name",
          placeholder: "My Connection",
          rules: {
            required: "This field is required",
          },
        },
        ...definition?.frontend?.connectionForm?.formData,
      ]
    : definition?.frontend?.connectionForm?.formData;

  return (
    <div className="flex flex-col w-full h-full">
      {/* Fixed Header */}
      <div className="w-full p-4 flex-shrink-0">
        <div className="flex justify-between w-full items-center mb-5">
          <div
            className="flex cursor-pointer gap-0 items-center text-[13px] text-muted-foreground"
            onClick={onBack}
          >
            {!isConnectionSelected && !isLoading && (
              <div className="flex gap-0 items-center">
                <ChevronLeft size={18} />
                <span>Back</span>
              </div>
            )}
          </div>
          <X
            size={16}
            className="cursor-pointer text-muted-foreground"
            style={{ width: '16px !important', height: '16px !important', minWidth: '16px', minHeight: '16px' }}
            onClick={onClose}
          />
        </div>
        
        {showAvatars ? (
          // OAuth Connection Layout
          <div className="w-full flex flex-col gap-4 items-center">
            {/* Avatar Section for OAuth with imageUrl, otherwise show integration image */}
            {platformImageUrl ? (
              <div className="flex items-center justify-center gap-3">
                <Avatar className="h-16 w-16 border-2 border-background">
                  <AvatarImage 
                    src={platformImageUrl} 
                    alt={definition?.frontend?.spec?.title}
                    className="p-2"
                  />
                  <AvatarFallback>{definition?.frontend?.spec?.title?.[0]}</AvatarFallback>
                </Avatar>
                <X 
                  size={18} 
                  className="text-muted-foreground"
                />
                <Avatar className="h-16 w-16 border-2 border-background">
                  <AvatarImage 
                    src="https://assets.picaos.com/connectors/pica.svg" 
                    alt="Pica"
                    className="p-2"
                  />
                  <AvatarFallback>P</AvatarFallback>
                </Avatar>
              </div>
            ) : (
              <img 
                src={definition?.frontend?.spec?.image} 
                alt={definition?.frontend?.spec?.title}
                className="w-12 h-12"
              />
            )}
            
            {/* Main Heading */}
            <h1 className="text-center text-[18px] font-medium px-4 text-foreground">
              {companyName ? `${companyName} uses Pica to connect your ${definition?.frontend?.spec?.title} account` : `Connect your ${definition?.frontend?.spec?.title} account`}
            </h1>
            
            {/* Only show dividers and trust sections if NO form elements */}
            {(!formData || formData.length === 0) && (
              <>
                {/* Divider */}
                <div className="w-full border-t border-border" />
                
                {/* Authenticate Instantly */}
                <div className="flex items-start gap-3 w-full px-4">
                  <Zap 
                    size={20} 
                    className="flex-shrink-0 mt-0.5 text-foreground"
                  />
                  <div className="flex flex-col gap-1">
                    <h3 className="font-semibold text-[15px] text-foreground">
                      Authenticate instantly
                    </h3>
                    <p className="text-[13px] text-muted-foreground">
                      Secure OAuth flow authenticates directly with your provider in seconds.
                    </p>
                  </div>
                </div>
                
                {/* Always Connected */}
                <div className="flex items-start gap-3 w-full px-4">
                  <RefreshCw 
                    size={20} 
                    className="flex-shrink-0 mt-0.5 text-foreground"
                  />
                  <div className="flex flex-col gap-1">
                    <h3 className="font-semibold text-[15px] text-foreground">
                      Always connected
                    </h3>
                    <p className="text-[13px] text-muted-foreground">
                      Authentication tokens refresh automatically so your integration stays active.
                    </p>
                  </div>
                </div>
                
                {/* Enterprise Security */}
                <div className="flex items-start gap-3 w-full px-4">
                  <Lock 
                    size={20} 
                    className="flex-shrink-0 mt-0.5 text-foreground"
                  />
                  <div className="flex flex-col gap-1">
                    <h3 className="font-semibold text-[15px] text-foreground">
                      Enterprise security
                    </h3>
                    <p className="text-[13px] text-muted-foreground">
                      Your credentials remain encrypted and are never exposed.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          // Non-OAuth Connection Layout (Original)
          <div className="w-full flex flex-col gap-3 items-center">
            <img 
              src={definition?.frontend?.spec?.image} 
              alt={definition?.frontend?.spec?.title}
              className="w-auto h-auto"
            />
            <h1 className="text-center text-[18px] font-semibold">
              {definition?.frontend?.spec?.title}
            </h1>
            <p className="text-center text-[13px] text-muted-foreground">
              {definition?.frontend?.spec?.category}
            </p>
          </div>
        )}
      </div>

          {/* Scrollable Form Area */}
          <div className="flex-1 overflow-y-auto scrollbar-hide px-4 flex flex-col">
            <form className={cn(
              "w-full flex-1 flex flex-col",
              showAvatars ? "justify-between" : ""
            )} onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col gap-5 pb-4">
                {/* Form Fields */}
                {formData && formData.length > 0 && (
                  <div className="w-full flex flex-col gap-0">
                    {formData?.map((form: any) => {
                    return (
                      <div
                        className={form.hidden ? "pt-0" : "pt-5"}
                        key={form.name}
                      >
                        {form?.type === "input" && (
                          <FormInput
                            name={form.name}
                            label={form.label}
                            placeholder={form.placeholder}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                            disabled={form.disabled}
                            value={form.value}
                            hidden={form.hidden}
                          />
                        )}
                        {form?.type === "select" && (
                          <FormSelect
                            name={form.name}
                            label={form.label}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                            options={form.options}
                            hidden={form.hidden}
                          />
                        )}
                        {form?.type === "number" && (
                          <FormNumberInput
                            name={form.name}
                            label={form.label}
                            placeholder={form.placeholder}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                            max={form.max}
                            min={form.min}
                            step={form.step}
                            value={form.value}
                          />
                        )}
                        {form?.type === "switch" && (
                          <FormSwitch
                            name={form.name}
                            label={form.label}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                            control={control}
                            hidden={form.hidden}
                          />
                        )}
                        {form?.type === "password" && (
                          <FormPassword
                            name={form.name}
                            label={form.label}
                            placeholder={form.placeholder}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                          />
                        )}
                        {form?.type === "textarea" && (
                          <FormTextArea
                            name={form.name}
                            label={form.label}
                            placeholder={form.placeholder}
                            rules={form.rules}
                            register={register}
                            errors={errors}
                          />
                        )}
                      </div>
                    );
                  })}
                  </div>
                )}
                
                {definition?.frontend?.spec?.markdown && (
                  <div className="max-w-full w-full border border-dashed rounded-md px-2 border-border">
                    <MarkdownRenderer
                      markdown={definition.frontend.spec.markdown}
                    />
                  </div>
                )}
              </div>
              
              {/* Connect Button Section */}
              {showAvatars ? (
                // OAuth: Button at bottom with loading state
                <div className="flex flex-col gap-4 pb-4">
                  <div className="flex flex-col items-center gap-4 w-full">
                    {error && (
                      <Alert variant="destructive" className="rounded-md w-full mb-2">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <AlertDescription>
                          {error}
                        </AlertDescription>
                      </Alert>
                    )}
                    
                    <Button
                      isLoading={isLoading}
                      type="submit"
                      className="w-full text-[13px]"
                    >
                      {isLoading ? "Connecting" : "Connect"}
                    </Button>
                    
                    {isLoading && (
                      <div className="flex items-center justify-center gap-1 text-[13px] text-muted-foreground">
                        <p>Having trouble connecting?</p>
                        <p
                          className="cursor-pointer underline font-bold text-foreground"
                          onClick={isConnectionSelected ? onClose : onBack}
                        >
                          Try again
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Non-OAuth: Original layout
                <>
                  {error && (
                    <Alert variant="destructive" className="rounded-md mb-4">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <AlertDescription>
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <Button
                    isLoading={isLoading}
                    type="submit"
                    className="w-full text-[13px] mb-4"
                  >
                    {isLoading ? "Connecting" : "Connect"}
                  </Button>
                </>
              )}
              
              {/* Non-OAuth: Loading state below button */}
              {!showAvatars && isLoading && (
                <div className="flex items-center justify-center gap-1 pb-4 text-[13px] mt-auto text-muted-foreground">
                  <p>Having trouble connecting?</p>
                  <p
                    className="cursor-pointer underline font-bold text-foreground"
                    onClick={isConnectionSelected ? onClose : onBack}
                  >
                    Try again
                  </p>
                </div>
              )}
            </form>
          </div>
      
      {helperLink && (
        <div
          className={cn(
            "flex items-center justify-center gap-2 min-h-[40px] px-4 py-2 border-t",
            "bg-subtle border-border",
            !isWhiteLabel && "border-b"
          )}
        >
          <VscQuestion
            className="text-foreground"
            size={18}
          />
          <div className="flex items-center gap-1 text-[13px]">
            <span className="text-foreground">
              Need help? View our
            </span>
            <a 
              href={helperLink} 
              target="_blank" 
              rel="noreferrer"
              className="font-bold underline inline-flex items-center gap-1 text-foreground"
            >
              Connection Guide
              <ExternalLink size={12} />
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
